import React, { useCallback, useState, useEffect, ReactNode } from 'react';
import _get from 'lodash.get';
import {
  TextField,
  MenuItem,
  ListItemIcon,
  Checkbox,
  Chip,
  TextFieldProps,
} from '@mui/material';
import { CloseIcon } from '../../../assets';
import { useStyles } from './inputs.styles';
import { SelectOption } from '../../../types/globalTypes';
import { InputChangeEventType } from '../../../types';

interface FormikProps {
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  values: Record<string, any>;
  touched?: Record<string, any>;
  errors?: Record<string, any>;
}

export interface SelectInputProps<T = string | number>
  extends Omit<TextFieldProps, 'onChange' | 'value' | 'name' | 'select'> {
  name: string;
  value?: T | T[];
  options: SelectOption<T>[];
  formikProps?: Partial<FormikProps>;
  setValue?: (value: T | T[]) => void;
  handleBlur?: (e: React.FocusEvent<any>) => void;
  onChange?: (e: React.ChangeEvent<any>) => void;
  onMouseEnter?: (e: React.MouseEvent<any>) => void;
  onMouseLeave?: (e: React.MouseEvent<any>) => void;
  placeholder?: string;
  ignoreFormikOnchange?: boolean;
  validationKey?: string | string[];
  useCustomValue?: boolean;
  idField?: string;
  displayField?: string;
  clearable?: boolean;
  renderWith?: 'chip' | 'text';
  slotProps?: any;
}

export function SelectInput<T = string | number>({
  name,
  value,
  useCustomValue = false,
  setValue,
  handleBlur,
  formikProps,
  onChange,
  onMouseEnter,
  onMouseLeave,
  placeholder,
  ignoreFormikOnchange,
  validationKey,
  ...otherProps
}: SelectInputProps<T>): React.ReactElement {
  const [focused, setFocused] = useState(false);

  const {
    handleChange: formikHandleChange,
    handleBlur: formikHandleBlur,
    values,
    touched,
    errors,
  } = formikProps || {};

  const hasMultiple = otherProps.slotProps?.select?.multiple;

  const handleChange = useCallback(
    (
      e: React.ChangeEvent<
        | HTMLInputElement
        | InputChangeEventType
        | { name?: string; value: unknown }
      >,
    ) => {
      onChange && onChange(e);
      !!formikHandleChange &&
        !ignoreFormikOnchange &&
        !useCustomValue &&
        formikHandleChange(e);
      setValue && setValue((e.target as any).value);
    },
    [
      formikHandleChange,
      ignoreFormikOnchange,
      onChange,
      setValue,
      useCustomValue,
    ],
  );

  const onBlur = useCallback(
    (e: React.FocusEvent<any>) => {
      formikHandleBlur && formikHandleBlur(e);
      handleBlur && handleBlur(e);
      setFocused(false);
    },
    [formikHandleBlur, handleBlur],
  );

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<any>) => {
      onMouseEnter && onMouseEnter(e);
      setFocused(true);
    },
    [onMouseEnter],
  );

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent<any>) => {
      onMouseLeave && onMouseLeave(e);
      setFocused(false);
    },
    [onMouseLeave],
  );

  const handleClear = useCallback(() => {
    const event = { target: { name, value: hasMultiple ? [] : '' } } as any;
    handleChange(event);
  }, [handleChange, hasMultiple, name]);

  // On blur touched is set on the name but on submit it's set on the nested object so we need to check both
  const isTouched = !!(
    _get(touched, name) || _get(touched, validationKey as string)
  );
  const errorText = _get(errors, validationKey ?? name) as string;

  return (
    <RenderSelectInput<T>
      name={name}
      value={
        formikProps !== undefined && !useCustomValue ? values?.[name] : value
      }
      onBlur={onBlur}
      onChange={handleChange}
      error={isTouched && !!errorText}
      helperText={isTouched ? errorText : undefined}
      handleClear={handleClear}
      handleMouseEnter={handleMouseEnter}
      handleMouseLeave={handleMouseLeave}
      focused={focused}
      placeholder={placeholder}
      {...otherProps}
    />
  );
}

interface RenderSelectInputProps<T = string | number>
  extends Omit<
    SelectInputProps<T>,
    | 'formikProps'
    | 'setValue'
    | 'ignoreFormikOnchange'
    | 'validationKey'
    | 'useCustomValue'
  > {
  error?: boolean;
  helperText?: ReactNode;
  handleClear?: () => void;
  handleMouseEnter?: (e: React.MouseEvent<any>) => void;
  handleMouseLeave?: (e: React.MouseEvent<any>) => void;
  focused?: boolean;
}

function RenderSelectInput<T = string | number>({
  name,
  value,
  options,
  label,
  handleBlur,
  className,
  onChange,
  onBlur,
  placeholder,
  error,
  helperText,
  idField = 'id',
  displayField = 'name',
  clearable,
  handleClear,
  handleMouseEnter,
  handleMouseLeave,
  focused,
  slotProps: _slotProps,
  renderWith,
  ...otherProps
}: RenderSelectInputProps<T>): React.ReactElement {
  const { classes, cx } = useStyles();
  const [valueObj, setValueObj] = useState<
    Record<string | number, string | ReactNode>
  >({});

  const {
    input = {},
    select = {},
    inputLabel = {},
    ...slotProps
  } = _slotProps || {};
  const hasMultiple = select.multiple;

  useEffect(() => {
    if (select.multiple) {
      const objById = options.reduce(
        (acc, cur) => {
          const value = (cur.enumInt ?? cur.id) as string | number;
          acc[value + ''] = cur.name;
          return acc;
        },
        {} as Record<string | number, string | ReactNode>,
      );
      setValueObj(objById);
    }
  }, [options, select.multiple]);

  return (
    <TextField
      variant='standard'
      select
      name={name}
      value={value as any}
      label={label}
      onChange={onChange}
      onBlur={onBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cx(classes.select, className)}
      error={error}
      helperText={helperText}
      size='small'
      slotProps={{
        input: {
          endAdornment:
            clearable && focused && !!value ? (
              <CloseIcon
                onClick={handleClear}
                className={classes.selectClearIcon}
              />
            ) : undefined,
          size: 'small',
          ...input,
        },
        select: {
          MenuProps: {
            disableScrollLock: true, // need this to stop the menu from jumping
          },
          renderValue:
            placeholder &&
            (!value || (Array.isArray(value) && value.length === 0))
              ? () => <span className={classes.placeholder}>{placeholder}</span>
              : hasMultiple && Array.isArray(value)
                ? renderWith === 'chip'
                  ? (selected: T[]) => (
                      <div className={classes.chips}>
                        {selected.map((val) => (
                          <Chip
                            key={val as any}
                            label={valueObj[val as any]}
                            className={classes.chip}
                          />
                        ))}
                      </div>
                    )
                  : (selected: T[]) =>
                      selected.map((v) => valueObj[v as any]).join(', ')
                : undefined,
          ...select,
        },
        inputLabel: {
          className: classes.label,
          ...inputLabel,
        },
        ...slotProps,
      }}
      {...otherProps}
    >
      {options?.map((c) => {
        const id = (c as any)[idField];
        const nameVal = (c as any)[displayField];
        return (
          <MenuItem key={id} value={id}>
            {hasMultiple && (
              <ListItemIcon>
                <Checkbox
                  size='small'
                  color='primary'
                  checked={Array.isArray(value) && value.includes(id)}
                />
              </ListItemIcon>
            )}
            {nameVal}
          </MenuItem>
        );
      })}
    </TextField>
  );
}
